import axios from "axios"
import Crypto, {AES, RSA} from "../libs/Crypto"
import {type IKeyVal} from "../shared/Password"
import EventEmitter from "events"
import * as _ from "lodash"
import Auth, {type IUserPassword} from "./Auth"
import History from "./History"
import Utils from "../libs/Utils"

const public_pem = `-----BEGIN PUBLIC KEY-----
MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAzI4rzXzGBPFrAIj0VH53
UJ0dLl9BNeD/JkBk4FAek+YZ0Eyx17WUyMrixWk6Ihg5oTbRN6wCxVaKTGJqGjvv
zOgbobpPKPJlQkFOxMQFH4dsKnXuEG0sy2Ib1BjxRfFyKjC9mRgltKTEyp5qCmSg
B6chrKiVKFbjYJBRNV8gpwViMvUISNBijh+/SnTKFtdxWqPstYx0b3jLvklnOLFI
vCufdxsmRrQi99VB/8aPElP3MN7+C/eWDbXH5+8d5WWCH0viDtilImZSvsQg41BG
cOwlVdwYFx/1hShi/5IncKfW92ZhUgCUoZ+kaZdpEuZGYN0ODMJqmzufGqpvqeHm
7/SlaBd8j5M6YKZkB5uSmUIb3C/ZX+pOub9qm/W25FEwNYXv2hfPBV7PjP7Uzs3q
BbSpCC+gM2qa0Scc4q0Ro75bsXxidhqQ0TzG9sH+HEhIbTim9rt62PXKdg/qxEfR
sndsN7oQagN4JkW6/lmWoBLS7VBK4JzSa1u/Qt0h12KyCLhQKGhLeQF+z62TzkXl
iwCkiXSQVFUGSHniUcOIOZP1W7CIkupjiw6Pb9T212W3HIil9mVcsFKsMXi5wPd9
JPrafnNYAEwpsLwk+F1uiLz1vUeEqtzN5rzJZ91VlCRVAFnP99H9rfx+1YBmEwza
iH9y2Nj1rFma7q466iJfj58CAwEAAQ==
-----END PUBLIC KEY-----`

function sessionKey(){
    const now = new Date()
    return Utils.formatDate("yyyyMMddHH", now) + ~~(now.getMinutes()/12)
}

export default class PassApi extends EventEmitter {
    static _i:PassApi
    static get i(){
        if (!PassApi._i) PassApi._i = new PassApi()
        if (document.location.hostname=="localhost") (window as any).api = PassApi._i
        return PassApi._i
    }

    private _host:string = "";
    private _protocol:string = "http"
    private _auth:Auth = new Auth()
    private _crypto:RSA = new RSA()
    private _history:History = new History()
    private _token:string = ""

    constructor(){
        super()
        if (document.location.hostname=="localhost") this._host = "localhost:3000";
        else {
            this._host = document.location.hostname
            this._protocol = "https"
        }
    }
    get isLoggedIn() { return !!this._token }
    get passwords(){ return this._auth.data }
    get headers(){ return {headers:{jwt:this._token,"content-type":"application/json"}}}
    private url(path:string) { return `${this._protocol}://${this._host}${path}`}

    private async _encrypt(password:string){
        return this._crypto.encrypt(await Crypto.sha256(password))
    }

    logout(){
        this._auth = new Auth()
        this._crypto = new RSA()
        this.emit("auth", false)
    }
    async login(email:string,pass:string,offline:boolean){
        await this._crypto.init(public_pem)
        if (offline){
            this._history.setOffline()
            return this.init(null, pass)
        }

        const password = await this._encrypt(pass)
        try {
            const {data} = await axios.post<string>(this.url("/1/users/login"), {email,password}, {timeout:1000})
            this._token = data
            return await this.sync(pass)
        }catch(e:any){
            if (e && (e.code=="ERR_NETWORK" || e.code=="ECONNABORTED" || e.status==502)) return this.init(null, pass)
            else throw e
        }
    }
    async register(email:string,pass:string){
        await this._crypto.init(public_pem)
        const password = await this._encrypt(pass)
        const {data} = await axios.post<string>(this.url("/1/users/register"), {email,password});
        this._token = data
        return await this.sync(pass)
    }
    async sync(password:string){
        return axios.post<IUserPassword>(this.url("/1/users/me/sync"), this._history.history, this.headers).then(this.auth(password))
    }
    private auth(password:string) {
        return async (data:{data:IUserPassword}) => this.init(data.data, password)
    }

    private async init(data:IUserPassword|null, password:string, remember?:boolean) {
        await this._auth.init(data, password)        
        if (data) this._history.flush()
        this.emit("auth", true)
        this.notify()

        if (remember) Crypto.generateKey().then(async (key) => {
            const {publicKey,privateKey} = key
            const rsa = await (new RSA()).init(publicKey, privateKey)
            const aes = await (new AES()).init(sessionKey())
            const value = await aes.encrypt(JSON.stringify({key:privateKey,pass:await rsa.encrypt(password)}))
            sessionStorage.setItem("sess", value)
        })
        return this
    }

    async resume(){
        try {
            const value = sessionStorage.getItem("sess")
            if (!value) throw {status:401}
    
            const aes = await (new AES()).init(sessionKey())
            const sess = JSON.parse(await aes.decrypt(value))
            const rsa = await (new RSA()).init("", sess.key)
            return this.init(null, await rsa.decrypt(sess.pass))
        }catch(e){
            sessionStorage.removeItem("sess")
            throw e
        }
    }

    // update password
    async updatePassword(old:string, pass:string){
        const data = await this._auth.updatePassword(old, pass)
        if (this._history.dirty) {
            for(const id in data) this._history.add(id, data[id])
        }
        else {
            try {
                const password = await this._encrypt(pass)
                await axios.put<number>(this.url("/1/users/me"), {password,data}, this.headers)
            }
            catch(e){
                for(const id in data) this._history.add(id, data[id])
            }
        }        
    }

    // create/update
    async create(name:string){
        const {id,value} = await this._auth.create(name)
        await this._update(id, value)
        this.notify()
    }

    async update(id:string, keyval:IKeyVal[]) {
        const value = await this._auth.update(id, keyval)
        await this._update(id, value)
        this.notify()
    }

    private async _update(id:string,value:string){
        if (this._history.dirty) this._history.add(id, value)
        else {
            try {
                await axios.put<number>(this.url("/1/users/me/data/"+id), value, this.headers)
            }catch(e){
                this._history.add(id, value)
            }
        }
    }

    // delete
    async delete(id:string){
        if (this._history.dirty) this._history.add(id, null)
        else {
            try {await axios.delete<number>(this.url("/1/users/me/data/"+id), this.headers)}
            catch(e){
                this._history.add(id, null)
            }
        }
        this._auth.delete(id)
        this.notify()
    }

    notify(){
        this.emit("refresh", this.passwords)
    }
    clear(){
        this._history.clear()
        this._auth.clear()
    }
}
